import { createSelector } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  CompanyContractStatusEnum,
  Country,
  RegistrationSource,
  UserCompanyPureType,
} from 'domain/api/graphql/generated';

import { selectUserId, selectLastFetchTime, selectCurrentCompanyId } from './selectors';
import { AccountUserShortData } from './types';

export const createAccountDataSelectors = (apolloClient: ApolloClient<NormalizedCacheObject>) => {
  const selectUserData = createSelector([selectUserId, selectLastFetchTime], (userId) => {
    if (!userId || !apolloClient) return null;

    try {
      const result = apolloClient.readQuery<AuthCurrentUserQuery>({
        query: AuthCurrentUserDocument,
      });

      const data = result?.currentUserPureQuery;

      if (!data) {
        console.warn('User not found in Apollo cache');

        return null;
      }

      const user: AccountUserShortData = {
        id: data?.id || '',
        email: data?.email || '',
        phone: data?.phone || '',
        firstName: data?.firstName || '',
        registrationSource: data?.registrationSource || RegistrationSource.Email,
      };

      return user;
    } catch (error) {
      console.warn('Failed to read user from Apollo cache:', error);

      return null;
    }
  });

  const selectUserRegistrationSource = createSelector(
    [selectUserData],
    (user: AccountUserShortData | null) => user?.registrationSource
  );

  const selectMainCompanyData = createSelector([selectCurrentCompanyId, selectLastFetchTime], () => {
    if (!apolloClient) return null;

    try {
      const result = apolloClient.readQuery<AuthCurrentUserQuery>({
        query: AuthCurrentUserDocument,
      });

      const mainCompany = result?.currentUserPureQuery?.mainCompany;

      if (!mainCompany) {
        console.warn('Main company not found in Apollo cache');

        return null;
      }

      return mainCompany as UserCompanyPureType;
    } catch (error) {
      console.warn('Failed to read main company:', error);

      return null;
    }
  });

  const selectCompanyIsBillingEnabled = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.isBillingEnabled ?? false
  );

  const selectCompanyCountry = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.country ?? Country.Ru
  );

  const selectActualContract = createSelector([selectMainCompanyData], (company: UserCompanyPureType | null) =>
    company?.contracts?.find((c) => c.status === CompanyContractStatusEnum.Actual)
  );

  const selectInvoices = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.invoices ?? []
  );

  const selectCompanyCanSberIdLink = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.canSberIdLink ?? false
  );

  const selectCompanySberIdWasLinked = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.sberIdWasLinked ?? false
  );

  const selectCompanyTrialPeriod = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.trialPeriod
  );

  const selectCompanyDeviceRequests = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.deviceRequests ?? []
  );

  const selectCompanyPricePlan = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.pricePlan
  );

  const selectCompanyRequisite = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.requisite
  );

  const selectCompanyStreamLibraryId = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => {
      const id = company?.streamLibrariesIds?.length ? String(company.streamLibrariesIds[0]) : undefined;

      return id;
    }
  );

  const selectCompanyUIFeatures = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.uiFeatures
  );

  const selectCompanyVideoDemoPeriod = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.videoDemoPeriod
  );

  const selectCompanyRegistrationSource = createSelector(
    [selectMainCompanyData],
    (company: UserCompanyPureType | null) => company?.registrationSource
  );

  return {
    selectUserData,
    selectUserRegistrationSource,
    selectMainCompanyData,
    selectCompanyIsBillingEnabled,
    selectCompanyCountry,
    selectActualContract,
    selectInvoices,
    selectCompanyCanSberIdLink,
    selectCompanySberIdWasLinked,
    selectCompanyTrialPeriod,
    selectCompanyDeviceRequests,
    selectCompanyPricePlan,
    selectCompanyRequisite,
    selectCompanyStreamLibraryId,
    selectCompanyUIFeatures,
    selectCompanyVideoDemoPeriod,
    selectCompanyRegistrationSource,
  };
};

export type AccountDataSelectors = ReturnType<typeof createAccountDataSelectors>;
